import { Search, ChevronDown } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { router, usePage } from "@inertiajs/react";
import { useState, useEffect } from "react";
import { Filters } from "@/types";

interface SearchHeaderProps {
  filters: Filters;
  vendors: string[];
  colors: string[];
}

export function SearchHeader({ filters, vendors, colors }: SearchHeaderProps) {
  const props = usePage().props
  const lists_count = props.lists_count as number;
  const [search, setSearch] = useState(filters.search || "");
  const [selectedVendors, setSelectedVendors] = useState<string[]>(filters.vendors || []);
  const [selectedColors, setSelectedColors] = useState<string[]>(filters.colors || []);
  const [showColorFilter, setShowColorFilter] = useState(false);
  const [showVendorFilter, setShowVendorFilter] = useState(false);
  const [isSearching, setIsSearching] = useState(false);

  // Debounced search effect
  useEffect(() => {
    const timer = setTimeout(() => {
      if (search !== filters.search || JSON.stringify(selectedVendors) !== JSON.stringify(filters.vendors) || JSON.stringify(selectedColors) !== JSON.stringify(filters.colors)) {
        router.get(
          "/products",
          { search, vendors: selectedVendors, colors: selectedColors },
          {
            preserveState: true,
            preserveScroll: true,
            onStart: () => setIsSearching(true),
            onFinish: () => setIsSearching(false),
          }
        );
      }
    }, 750);

    return () => clearTimeout(timer);
  }, [search, selectedVendors, selectedColors]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
  };

  const clearSearch = () => {
    setSearch("");
    setSelectedVendors([]);
    setSelectedColors([]);
    router.get("/products");
  };

  const handleVendorToggle = (vendor: string) => {
    setSelectedVendors(prev => 
      prev.includes(vendor) 
        ? prev.filter(v => v !== vendor)
        : [...prev, vendor]
    );
  };

  const clearVendorFilter = () => {
    setSelectedVendors([]);
  };

  const handleColorToggle = (color: string) => {
    setSelectedColors(prev => 
      prev.includes(color)
        ? prev.filter(c => c !== color)
        : [...prev, color]
    );
  };

  const clearColorFilter = () => {
    setSelectedColors([]);
  };

  return (
    <header className="border-b border-border bg-card">
      <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
        <div className="flex flex-col gap-6">
          <div className="flex items-center justify-between">
            <h1 className="font-serif text-2xl font-bold tracking-tight text-foreground sm:text-3xl">
              Gadżety Reklamowe
            </h1>
            <div className="flex gap-3">
              <Button
                variant="outline"
                size="sm"
                className="hidden sm:flex bg-transparent hover:cursor-pointer"
                onClick={() => {
                  router.visit(
                    "/forms",
                  );
                }} >
                Formularz
              </Button>
              <div className="relative">
                <Button
                  variant="outline"
                  size="sm"
                  className="hidden sm:flex bg-transparent hover:cursor-pointer relative"
                  onClick={() => router.visit("/lists")}
                >
                  Lista
                </Button>

                {lists_count > 0 && (
                  <span className="
                    absolute -top-2 -right-2
                    flex h-5 min-w-5 items-center justify-center
                    rounded-full bg-red-500 px-1
                    text-xs font-medium text-white
                  ">
                    {lists_count}
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="relative">
            <Search className="absolute left-3 top-1/2 size-5 -translate-y-1/2 text-muted-foreground" />
            <Input
              type="search"
              value={search}
              onChange={handleSearchChange}
              placeholder="Szukaj kubków, długopisów, gadżetów..."
              className="h-12 pl-11 pr-4 text-base"
            />

            {/* Loading indicator */}
            {isSearching && (
              <div className="absolute right-3.5 top-1/2 -translate-y-1/2">
                <div className="h-5 w-5 animate-spin rounded-full border-2 border-primary border-t-transparent" />
              </div>
            )}
          </div>

        <div className="flex gap-3">
          {/* Vendor Filter */}
          <div className="relative">
            <button
              onClick={() => setShowVendorFilter(!showVendorFilter)}
              className="flex items-center gap-2 px-4 py-2 border border-border rounded-md hover:bg-accent transition"
            >
              <span className="text-sm font-medium">Vendorzy</span>
              {selectedVendors.length > 0 && (
                <span className="ml-2 px-2 py-0.5 bg-primary text-primary-foreground text-xs rounded-full">
                  {selectedVendors.length}
                </span>
              )}
              <ChevronDown className="h-4 w-4" />
            </button>

            {/* Vendor dropdown */}
            {showVendorFilter && (
              <div className="absolute top-full left-0 mt-2 w-80 bg-card border border-border rounded-md shadow-lg z-50 p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-sm">Wybierz vendorów</h3>
                  {selectedVendors.length > 0 && (
                    <button
                      onClick={clearVendorFilter}
                      className="text-xs text-primary hover:underline"
                    >
                      Wyczyść
                    </button>
                  )}
                </div>
                <div className="max-h-64 overflow-y-auto space-y-2">
                  {vendors.length > 0 ? (
                    vendors.map(vendor => (
                      <label key={vendor} className="flex items-center gap-2 cursor-pointer p-2 hover:bg-accent rounded transition">
                        <input
                          type="checkbox"
                          checked={selectedVendors.includes(vendor)}
                          onChange={() => handleVendorToggle(vendor)}
                          className="w-4 h-4 rounded border-border"
                        />
                        <span className="text-sm">{vendor}</span>
                      </label>
                    ))
                  ) : (
                    <p className="text-sm text-muted-foreground">Brak vendorów</p>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Color Filter */}
          <div className="relative">
            <button
              onClick={() => setShowColorFilter(!showColorFilter)}
              className="flex items-center gap-2 px-4 py-2 border border-border rounded-md hover:bg-accent transition"
            >
              <span className="text-sm font-medium">Kolory</span>
              {selectedColors.length > 0 && (
                <span className="ml-2 px-2 py-0.5 bg-primary text-primary-foreground text-xs rounded-full">
                  {selectedColors.length}
                </span>
              )}
              <ChevronDown className="h-4 w-4" />
            </button>

            {/* Color dropdown */}
            {showColorFilter && (
              <div className="absolute top-full left-0 mt-2 w-80 bg-card border border-border rounded-md shadow-lg z-50 p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-sm">Wybierz kolory</h3>
                  {selectedColors.length > 0 && (
                    <button
                      onClick={clearColorFilter}
                      className="text-xs text-primary hover:underline"
                    >
                      Wyczyść
                    </button>
                  )}
                </div>
                <div className="max-h-64 overflow-y-auto space-y-2">
                  {colors.length > 0 ? (
                    colors.map(color => (
                      <label key={color} className="flex items-center gap-2 cursor-pointer p-2 hover:bg-accent rounded transition">
                        <input
                          type="checkbox"
                          checked={selectedColors.includes(color)}
                          onChange={() => handleColorToggle(color)}
                          className="w-4 h-4 rounded border-border"
                        />
                        <span className="text-sm">{color}</span>
                      </label>
                    ))
                  ) : (
                    <p className="text-sm text-muted-foreground">Brak kolorów</p>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
        
          {/* Optional: Show active search */}
          {search && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <span>Wyszukiwanie:</span>
              <span className="font-medium text-foreground">"{search}"</span>
              <button
                onClick={clearSearch}
                className="text-primary hover:underline"
              >
                Wyczyść
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
